import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLexware } from '../../context/LexwareContext';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

const { 
  FiActivity, 
  FiShoppingCart, 
  FiUser, 
  FiPackage, 
  FiUploadCloud, 
  FiDownloadCloud,
  FiFileText,
  FiUsers,
  FiCheckCircle,
  FiAlertCircle,
  FiEdit,
  FiTrash2,
  FiRefreshCw
} = FiIcons;

function RecentActivities() {
  const { data } = useLexware();

  // Icon und Farbe basierend auf Log-Typ
  const getActivityStyle = (type) => {
    const styles = {
      'IMPORT_SALES': { icon: FiShoppingCart, color: 'green', bg: 'bg-green-50', text: 'text-green-600' },
      'IMPORT_STAMMDATEN': { icon: FiUsers, color: 'blue', bg: 'bg-blue-50', text: 'text-blue-600' },
      'CLOUD_SYNC': { icon: FiUploadCloud, color: 'purple', bg: 'bg-purple-50', text: 'text-purple-600' },
      'CLOUD_RESTORE': { icon: FiDownloadCloud, color: 'indigo', bg: 'bg-indigo-50', text: 'text-indigo-600' },
      'RESTORE': { icon: FiRefreshCw, color: 'orange', bg: 'bg-orange-50', text: 'text-orange-600' },
      'TASK_CREATED': { icon: FiFileText, color: 'blue', bg: 'bg-blue-50', text: 'text-blue-600' },
      'TASK_COMPLETED': { icon: FiCheckCircle, color: 'green', bg: 'bg-green-50', text: 'text-green-600' },
      'CONTACT_UPDATED': { icon: FiEdit, color: 'yellow', bg: 'bg-yellow-50', text: 'text-yellow-600' },
      'CONTACT_DELETED': { icon: FiTrash2, color: 'red', bg: 'bg-red-50', text: 'text-red-600' },
      'AUTO_BACKUP_TOGGLE': { icon: FiRefreshCw, color: 'gray', bg: 'bg-gray-50', text: 'text-gray-600' },
      'DEFAULT': { icon: FiActivity, color: 'gray', bg: 'bg-gray-50', text: 'text-gray-600' }
    };
    return styles[type] || styles['DEFAULT'];
  };

  // Logs filtern (Login/Logout ausschließen) und limitieren
  const activities = useMemo(() => {
    const logs = data.logs || [];
    return logs
      .filter(log => log.type !== 'LOGIN' && log.type !== 'LOGOUT') // Login/Logout entfernen
      .slice(0, 15) // Maximal 15 Einträge
      .map(log => ({
        id: log.id,
        type: log.type,
        user: log.user,
        message: log.details || log.type,
        timestamp: log.timestamp,
        metadata: log.metadata
      }));
  }, [data.logs]);

  // Zeitformatierung (z.B. "vor 5 Min")
  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: de });
    } catch {
      return 'kürzlich';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col h-full"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 bg-gray-50 rounded-lg">
          <SafeIcon icon={FiActivity} className="text-gray-600 text-sm" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Aktivitäten</h3>
          <p className="text-[10px] text-gray-400 font-medium">Neueste zuerst</p>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
        {activities.length > 0 ? (
          activities.map((activity, index) => {
            const style = getActivityStyle(activity.type);
            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${style.bg}`}>
                  <SafeIcon icon={style.icon} className={`text-xs ${style.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-900 leading-tight truncate">
                      {activity.message}
                    </p>
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      {activity.user}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5">{formatTime(activity.timestamp)}</p>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-xs font-medium">
            Keine Aktivitäten vorhanden
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default RecentActivities;